import React, { useState, useEffect } from 'react';
import { Mail, Phone, Clock, ArrowRight, Linkedin, Twitter, Github, CheckCircle, MessageSquare, Calendar, Zap, Paperclip, X } from 'lucide-react';
import Reveal from '../components/Reveal';
import { submitInquiry, getSettings } from '../services/api';
import { validateFile, ATTACHMENT_EXTENSIONS } from '../utils/fileValidation';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';

const c = '#3b82f6';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', service: '', budget: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [focused, setFocused] = useState('');
  const [settings, setSettings] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');

  useEffect(() => {
    getSettings().then(setSettings).catch(() => {});
    if (window.location.hash === '#contact-form') {
      setTimeout(() => {
        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleAttachmentChange = (e) => {
    const file = e.target.files[0] || null;
    e.target.value = ''; // allow re-picking the same file after removal

    if (!file) return;

    const { valid, error } = validateFile(file, ATTACHMENT_EXTENSIONS);
    if (!valid) {
      setAttachment(null);
      setAttachmentError(error);
      return;
    }

    setAttachment(file);
    setAttachmentError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      let attachmentUrl = '';
      if (attachment) {
        const uploaded = await uploadToCloudinary(attachment, 'codinity/attachments');
        attachmentUrl = uploaded.secure_url;
      }

      await submitInquiry({
        fullName: form.name,
        email: form.email,
        service: form.service,
        budget: form.budget,
        message: form.message,
        attachmentUrl,
        attachmentOriginalName: attachment ? attachment.name : '',
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message || 'Failed to send. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = (field) => ({
    width: '100%',
    background: 'rgba(15,23,42,0.8)',
    border: `1.5px solid ${focused === field ? c : 'rgba(30,41,59,0.9)'}`,
    borderRadius: '0.625rem',
    padding: '0.9rem 1.1rem',
    color: '#fff',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  });

  const selectStyle = (field) => ({
    ...inputStyle(field),
    appearance: 'none',
    WebkitAppearance: 'none',
    cursor: 'pointer',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 1rem center',
    paddingRight: '2.5rem',
  });

  const steps = [
    { num: '01', icon: <MessageSquare style={{ width: '1.6rem', height: '1.6rem' }} />, title: 'Submit Your Inquiry', desc: 'Fill out the form with your project details, goals, and any existing work. The more context you share, the better we can help.' },
    { num: '02', icon: <Calendar style={{ width: '1.6rem', height: '1.6rem' }} />, title: '30-Min Discovery Call', desc: 'We schedule a free, no-obligation call to understand your vision, requirements, and answer any technical questions you have.' },
    { num: '03', icon: <Zap style={{ width: '1.6rem', height: '1.6rem' }} />, title: 'Receive Your Proposal', desc: 'Get a detailed scope, timeline, and fixed-price quote tailored to your project within 48 hours of the discovery call.' },
  ];

  const email = settings?.company?.email || 'info@codinity.net';
  const phone = settings?.company?.phone || '+92 3115392857';
  const responseTime = settings?.company?.responseTime || 'Within 24 hours';
  const availability = settings?.company?.availability || 'Currently accepting new projects';
  const workingHours = settings?.company?.workingHours || 'Monday - Friday, 9am - 6pm';
  const timezone = settings?.company?.timezone || 'GMT+5';

  const infoCards = [
    { icon: <Mail style={{ width: '1.4rem', height: '1.4rem' }} />, label: 'Email Us', value: email, href: `mailto:${email}` },
    { icon: <Phone style={{ width: '1.4rem', height: '1.4rem' }} />, label: 'Call Us', value: phone, href: `tel:${phone.replace(/\D/g, '')}` },
    { icon: <Clock style={{ width: '1.4rem', height: '1.4rem' }} />, label: 'Response Time', value: responseTime, href: null },
  ];

  const socials = [
    settings?.social?.linkedin && { icon: <Linkedin style={{ width: '1.2rem', height: '1.2rem' }} />, href: settings.social.linkedin, label: 'LinkedIn' },
    settings?.social?.twitter && { icon: <Twitter style={{ width: '1.2rem', height: '1.2rem' }} />, href: settings.social.twitter, label: 'Twitter' },
    settings?.social?.github && { icon: <Github style={{ width: '1.2rem', height: '1.2rem' }} />, href: settings.social.github, label: 'GitHub' },
  ].filter(Boolean);

  return (
    <div style={{ background: '#020617', color: '#fff' }}>

      {/* ══════════════════════════════════════════════════
          HERO  —  full-screen
      ══════════════════════════════════════════════════ */}
      <section style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        overflow: 'hidden',
      }}>

        {/* Background layers */}
        <div style={{ position: 'absolute', inset: 0 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(59,130,246,0.09) 0%, transparent 60%)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(2,6,23,1) 0%, transparent 55%)' }} />
          <div style={{ position: 'absolute', top: '15%', left: '5%', width: '38rem', height: '38rem', borderRadius: '50%', background: 'rgba(59,130,246,0.07)', filter: 'blur(120px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: '25%', right: '8%', width: '28rem', height: '28rem', borderRadius: '50%', background: 'rgba(139,92,246,0.06)', filter: 'blur(100px)', pointerEvents: 'none' }} />
        </div>

        {/* Grid pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(to right, #1e293b 1px, transparent 1px), linear-gradient(to bottom, #1e293b 1px, transparent 1px)',
          backgroundSize: '4rem 4rem',
          maskImage: 'radial-gradient(ellipse 90% 80% at 50% 40%, #000 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 80% at 50% 40%, #000 40%, transparent 100%)',
          opacity: 0.3,
        }} />

        <div style={{ position: 'relative', zIndex: 10, width: '100%', padding: 'clamp(7rem, 12vw, 11rem) clamp(1.5rem, 5vw, 5rem) clamp(4rem, 6vw, 6rem)' }}>

          {/* Centered hero content */}
          <div style={{ maxWidth: '56rem', margin: '0 auto', textAlign: 'center' }}>

            <Reveal direction="up">
              <div style={{ display: 'inline-flex', alignItems: 'center', padding: '0.35rem 1.1rem', borderRadius: '9999px', border: `1px solid ${c}35`, background: `${c}10`, marginBottom: '2rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: c, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Codinity · Contact</span>
              </div>
            </Reveal>

            <Reveal direction="up" delay={100}>
              <h1 style={{ fontSize: 'clamp(2.8rem, 6vw, 5rem)', fontWeight: 800, lineHeight: 1.06, color: '#fff', marginBottom: '1.5rem', letterSpacing: '-0.03em' }}>
                Let's Build Something
                <br />
                <span style={{ background: 'linear-gradient(90deg, #60a5fa, #818cf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Great Together
                </span>
              </h1>
            </Reveal>

            <Reveal direction="up" delay={200}>
              <p style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)', color: 'rgba(148,163,184,0.85)', maxWidth: '34rem', margin: '0 auto 3rem', lineHeight: 1.85 }}>
                Tell us about your project. We respond within 24 hours with honest advice, no sales pitch, no pressure.
              </p>
            </Reveal>

            <Reveal direction="up" delay={300}>
              <div style={{ display: 'flex', gap: '2.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                {['Free consultation', '24h response', 'Fixed-price quotes'].map((t, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', fontSize: '0.88rem', color: '#64748b' }}>
                    <CheckCircle style={{ width: '1rem', height: '1rem', color: c }} />
                    {t}
                  </div>
                ))}
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          HOW IT WORKS  —  full-screen
      ══════════════════════════════════════════════════ */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: 'clamp(5rem, 8vw, 7rem) 0',
        background: '#020617',
      }}>
        <div style={{ maxWidth: 'min(100rem, calc(100% - clamp(1.5rem, 5vw, 6rem)))', margin: '0 auto', padding: '0 clamp(1.5rem, 5vw, 5rem)', width: '100%' }}>

          {/* Section header */}
          <Reveal direction="up">
            <div style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 800, color: `${c}90`, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>THE PROCESS</div>
              <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.025em' }}>How It Works</h2>
              <p style={{ fontSize: '1rem', color: '#64748b', marginTop: '1rem', maxWidth: '36rem', margin: '1rem auto 0', lineHeight: 1.8 }}>
                Three simple steps from first message to project kickoff.
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '2rem' }}>
            {steps.map((step, i) => (
              <Reveal key={i} direction="up" delay={i * 130}>
                <div style={{
                  position: 'relative',
                  background: 'rgba(15,23,42,0.75)',
                  border: '1px solid rgba(30,41,59,0.9)',
                  borderRadius: '1.5rem',
                  padding: 'clamp(2rem, 3vw, 3rem) clamp(1.75rem, 2.5vw, 2.5rem)',
                  borderTop: `3px solid ${c}`,
                  height: '100%',
                  boxSizing: 'border-box',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{ width: '3.25rem', height: '3.25rem', borderRadius: '0.875rem', background: `${c}15`, color: c, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${c}30`, flexShrink: 0 }}>
                      {step.icon}
                    </div>
                    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: `${c}80`, letterSpacing: '0.1em' }}>STEP {step.num}</span>
                  </div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.85rem', lineHeight: 1.3 }}>{step.title}</h3>
                  <p style={{ fontSize: '0.9rem', color: '#64748b', lineHeight: 1.8 }}>{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          FORM + INFO  —  full-screen
      ══════════════════════════════════════════════════ */}
      <section id="contact-form" style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: 'clamp(5rem, 8vw, 7rem) 0',
      }}>
        <div style={{ maxWidth: 'min(100rem, calc(100% - clamp(1.5rem, 5vw, 6rem)))', margin: '0 auto', padding: '0 clamp(1.5rem, 5vw, 5rem)', width: '100%' }}>

          {/* Section header */}
          <Reveal direction="up">
            <div style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 800, color: `${c}90`, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>GET IN TOUCH</div>
              <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.025em' }}>Send Us a Message</h2>
              <p style={{ fontSize: '1rem', color: '#64748b', marginTop: '1rem', maxWidth: '36rem', margin: '1rem auto 0', lineHeight: 1.8 }}>
                Fill in the details below and we'll get back to you within one business day.
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '3rem', alignItems: 'start' }}>

            {/* ── FORM ── */}
            <Reveal direction="left">
              <div style={{ background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(30,41,59,0.9)', borderRadius: '1.75rem', padding: 'clamp(2rem, 4vw, 3rem)' }}>

                {submitted ? (
                  <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
                    <div style={{ width: '5rem', height: '5rem', borderRadius: '50%', background: `${c}18`, border: `2px solid ${c}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem' }}>
                      <CheckCircle style={{ width: '2.5rem', height: '2.5rem', color: c }} />
                    </div>
                    <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '1rem' }}>Message Sent!</h3>
                    <p style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: 1.8, marginBottom: '2rem' }}>
                      Thanks for reaching out. We'll review your message and respond within 24 hours.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setForm({ name: '', email: '', service: '', budget: '', message: '' }); setAttachment(null); setAttachmentError(''); }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: c, background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                    >
                      Send another message <ArrowRight style={{ width: '0.9rem', height: '0.9rem' }} />
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.35rem' }}>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1.1rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.5rem', letterSpacing: '0.04em' }}>FULL NAME *</label>
                        <input required type="text" placeholder="Alex Johnson" value={form.name} onChange={set('name')}
                          style={inputStyle('name')} onFocus={() => setFocused('name')} onBlur={() => setFocused('')} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.5rem', letterSpacing: '0.04em' }}>EMAIL ADDRESS *</label>
                        <input required type="email" placeholder="alex@company.com" value={form.email} onChange={set('email')}
                          style={inputStyle('email')} onFocus={() => setFocused('email')} onBlur={() => setFocused('')} />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.5rem', letterSpacing: '0.04em' }}>SERVICE INTERESTED IN</label>
                      <select value={form.service} onChange={set('service')} style={selectStyle('service')}
                        onFocus={() => setFocused('service')} onBlur={() => setFocused('')}>
                        <option value="" style={{ background: '#0f172a' }}>Select a service…</option>
                        <option value="ai" style={{ background: '#0f172a' }}>AI & Automation</option>
                        <option value="custom" style={{ background: '#0f172a' }}>Custom Software</option>
                        <option value="web" style={{ background: '#0f172a' }}>Web Development</option>
                        <option value="mobile" style={{ background: '#0f172a' }}>Mobile App Development</option>
                        <option value="cloud" style={{ background: '#0f172a' }}>Cloud & Backend</option>
                        <option value="other" style={{ background: '#0f172a' }}>Other / Not Sure Yet</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.5rem', letterSpacing: '0.04em' }}>ESTIMATED BUDGET</label>
                      <select value={form.budget} onChange={set('budget')} style={selectStyle('budget')}
                        onFocus={() => setFocused('budget')} onBlur={() => setFocused('')}>
                        <option value="" style={{ background: '#0f172a' }}>Select a range…</option>
                        <option value="small" style={{ background: '#0f172a' }}>Under $5,000</option>
                        <option value="medium" style={{ background: '#0f172a' }}>$5,000 - $15,000</option>
                        <option value="large" style={{ background: '#0f172a' }}>$15,000 - $50,000</option>
                        <option value="enterprise" style={{ background: '#0f172a' }}>$50,000+</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.5rem', letterSpacing: '0.04em' }}>TELL US ABOUT YOUR PROJECT *</label>
                      <textarea required rows={5} placeholder="Describe what you're looking to build, any existing work, timelines, or goals…"
                        value={form.message} onChange={set('message')}
                        style={{ ...inputStyle('message'), resize: 'none', fontFamily: 'inherit' }}
                        onFocus={() => setFocused('message')} onBlur={() => setFocused('')} />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.5rem', letterSpacing: '0.04em' }}>ATTACHMENT (OPTIONAL - PDF, WORD, PNG, JPG · MAX 5MB)</label>
                      <label style={{ ...inputStyle('attachment'), display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', color: attachment ? '#4ade80' : '#64748b' }}>
                        <Paperclip style={{ width: '1rem', height: '1rem', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {attachment ? attachment.name : 'Attach a file'}
                        </span>
                        {attachment && (
                          <button
                            type="button"
                            aria-label="Remove attachment"
                            onClick={(e) => { e.preventDefault(); setAttachment(null); setAttachmentError(''); }}
                            style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', padding: '2px' }}
                          >
                            <X style={{ width: '0.9rem', height: '0.9rem' }} />
                          </button>
                        )}
                        <input type="file" accept={ATTACHMENT_EXTENSIONS.join(',')} onChange={handleAttachmentChange} style={{ display: 'none' }} />
                      </label>
                      {attachmentError && (
                        <div style={{ color: '#f87171', fontSize: '0.8rem', marginTop: '0.4rem' }}>{attachmentError}</div>
                      )}
                    </div>

                    {submitError && (
                      <div style={{ color: '#f87171', fontSize: '0.875rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.5rem', padding: '0.75rem 1rem' }}>
                        {submitError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', padding: '1.1rem 2.25rem', background: submitting ? '#1d4ed8' : c, color: '#fff', border: 'none', borderRadius: '0.75rem', fontWeight: 700, fontSize: '1rem', cursor: submitting ? 'not-allowed' : 'pointer', boxShadow: `0 8px 30px ${c}35`, transition: 'opacity 0.2s, transform 0.2s', marginTop: '0.25rem', opacity: submitting ? 0.8 : 1 }}
                      onMouseEnter={e => { if (!submitting) { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = submitting ? '0.8' : '1'; e.currentTarget.style.transform = 'none'; }}
                    >
                      {submitting ? 'Sending...' : 'Send Message'} {!submitting && <ArrowRight style={{ width: '1.1rem', height: '1.1rem' }} />}
                    </button>

                  </form>
                )}
              </div>
            </Reveal>

            {/* ── INFO COLUMN ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

              {infoCards.map((card, i) => (
                <Reveal key={i} direction="right" delay={i * 100}>
                  <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(30,41,59,0.9)', borderRadius: '1.25rem', padding: '1.75rem', display: 'flex', alignItems: 'center', gap: '1.5rem', transition: 'border-color 0.2s, transform 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = `${c}40`; e.currentTarget.style.transform = 'translateX(5px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(30,41,59,0.9)'; e.currentTarget.style.transform = 'none'; }}>
                    <div style={{ width: '3.5rem', height: '3.5rem', borderRadius: '1rem', background: `${c}12`, color: c, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1px solid ${c}25` }}>
                      {card.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.73rem', fontWeight: 600, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.3rem' }}>{card.label}</div>
                      {card.href ? (
                        <a href={card.href} style={{ fontSize: '1rem', fontWeight: 600, color: '#e2e8f0', textDecoration: 'none', transition: 'color 0.2s' }}
                          onMouseEnter={e => e.currentTarget.style.color = c}
                          onMouseLeave={e => e.currentTarget.style.color = '#e2e8f0'}>
                          {card.value}
                        </a>
                      ) : (
                        <div style={{ fontSize: '1rem', fontWeight: 600, color: '#e2e8f0' }}>{card.value}</div>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}

              {/* Availability badge */}
              <Reveal direction="right" delay={300}>
                <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(30,41,59,0.9)', borderRadius: '1.25rem', padding: '1.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.9rem' }}>
                    <div style={{ width: '0.65rem', height: '0.65rem', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 10px #22c55e90', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#22c55e' }}>{availability}</span>
                  </div>
                  <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.7 }}>
                    Available {workingHours} ({timezone}). We respond to all inquiries within one business day.
                  </p>
                </div>
              </Reveal>

              {/* Social links */}
              <Reveal direction="right" delay={400}>
                <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(30,41,59,0.9)', borderRadius: '1.25rem', padding: '1.75rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '1.1rem' }}>Find Us Online</div>
                  <div style={{ display: 'flex', gap: '0.85rem' }}>
                    {socials.map((s, i) => (
                      <a key={i} href={s.href} target="_blank" rel="noopener noreferrer"
                        style={{ width: '3rem', height: '3rem', background: 'rgba(30,41,59,0.7)', border: '1px solid rgba(30,41,59,0.9)', borderRadius: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', textDecoration: 'none', transition: 'border-color 0.2s, color 0.2s, transform 0.2s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = `${c}50`; e.currentTarget.style.color = c; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(30,41,59,0.9)'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.transform = 'none'; }}>
                        {s.icon}
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>

            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
